import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../ui/Button/Button';
import { Popover } from '../../ui/Popover/Popover';
import { HELP_COPY, FIRST_RUN_HREF } from '../../routes/home/firstRunCopy';
import { testId } from '../testId';
import styles from './AppShell.module.css';

// HELP — the top bar's last control, which `_shared/web-shell.html` and every screen mockup have drawn since
// the shell was designed (`<button class="btn btn--ghost btn--icon" aria-label="Help">`) and which the web
// did not have. **REQ-005 AC1 is what needed it**: the first-run cards are "re-openable from Help", and w11's
// own foot says so in as many words ("You can reopen this any time from Help.").
//
// It holds exactly one entry today, and says what that entry is for rather than pretending to be a help
// centre we do not have. "Show me around again" goes to `/home?firstRun=1`, which is the one address that
// re-opens the cards whatever `firstRun.show` says (`routes/home/FirstRun.tsx`).
//
// A `Popover`, not a `role="menu"`: it carries a sentence as well as a command, which is the same reason the
// mode menu is one (REQ-006 AC2). Escape and an outside click close it and focus returns to the trigger (K6).

export function HelpMenu() {
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        iconOnly
        icon="help-circle"
        className={styles.helpTrigger}
        aria-label={HELP_COPY.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        testID={testId('shell', 'help')}
        onClick={() => setOpen((was) => !was)}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        id={panelId}
        placement="bottom-end"
        width={320}
        title={HELP_COPY.title}
        testID={testId('shell', 'help-menu')}
      >
        <div className={styles.helpEntry}>
          <Button
            block
            icon="graduation-cap"
            testID={testId('first-run', 'help')}
            onClick={() => {
              setOpen(false);
              void navigate(FIRST_RUN_HREF);
            }}
          >
            {HELP_COPY.firstRun}
          </Button>
          <p className={styles.helpHint}>{HELP_COPY.firstRunHint}</p>
        </div>
      </Popover>
    </>
  );
}
